/**
 * Helper: Multi-store
 * -----------------------------------------------------------------------------
 * Handles multi-store form actions and return to parameters.
 *
 */

/**
 * Get multi-store form action.
 * @param {Object} instance - Selected store instance settings.
 * @returns {String}
 */
export function multiStoreAction(instance) {
  if (instance?.url) {
    return `${instance.url}localization`
  }

  return '/localization'
}

/**
 * Get multi-store return URL including search parameters and hash.
 * - Adds redirect URL parameters when store has instance settings.
 * @param {Object} store - Selected/suggested store settings.
 * @param {Boolean} [root] - Redirect to the root instead of current path.
 * @returns {String}
 */
export function multiStoreReturnTo(store, root = true) {
  let currentUrl = root ? location.pathname : '/'
  const urlParameters = new URLSearchParams(location.search);

  /**
   * Remove existing country and language parameters.
   * - Also remove un-desired parameters.
   */
  ['_ab', '_fd', '_sc', 'country', 'language', 'shpxid'].forEach((parameter) => {
    urlParameters.delete(parameter)
  })

  if (!store || !store.country || !store.language) {
    currentUrl += urlParametersToString(urlParameters)
    currentUrl += location.hash
    return currentUrl
  }

  /**
   * Remove locale folder from URL.
   */
  if (cnvs.routes.root !== '/' && currentUrl.startsWith(cnvs.routes.root)) {
    currentUrl = `/${currentUrl.split(cnvs.routes.root)[1]}`
    currentUrl = currentUrl.replace('//', '/')
  }

  /**
   * Remove prefixed '/' when instance URL has value.
   * - Append redirect URL parameters.
   */
  if (store.instance?.url) {
    const formattedUrl = currentUrl.slice(0, 1) === '/'
      ? currentUrl.slice(1)
      : currentUrl

    currentUrl = `${store.instance.url}${formattedUrl}`

    urlParameters.append('country', store.country.code)
    urlParameters.append('language', store.language.code)
  }

  currentUrl += urlParametersToString(urlParameters)
  currentUrl += location.hash
  return currentUrl
}

/**
 * Replace multi-store strings.
 * @param {Object} [country] - Country object.
 * @param {Object} [language] - Language object.
 * @returns {Objects}
 */
export function replaceMultiStoreStrings(country, language) {
  let countryStrings = {}
  let languageStrings = {}

  if (country) {
    countryStrings = {
      country_code: country.code,
      country_name: country.name,
      currency_code: country.currency,
      currency_symbol: country.currencySymbol,
      the_country_code: country.the
        ? `${cnvs.strings.general.multi_store.the} ${country.code}`
        : country.code,
      the_country_name: country.the
        ? `${cnvs.strings.general.multi_store.the} ${country.name}`
        : country.name,
    }
  }

  if (language) {
    languageStrings = {
      language_code: language.code,
      language_name: language.name,
    }
  }

  return {
    ...countryStrings,
    ...languageStrings,
  }
}

/**
 * Converts URL parameters to string without changing encoding.
 * - Resolves issue where parameter value with a space is changed to +.
 * - Prefixes string with '?' if it has length.
 * @param {Object} parameters - URL search parameters.
 * @returns {String}
 */
function urlParametersToString(parameters) {
  const strings = []

  parameters.forEach((value, key) => {
    strings.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
  })

  return strings.length ? `?${strings.join('&')}` : ''
}
